<script lang="ts" setup>
import { computed, toRef } from 'vue';
import { getDefaultLocaleValue } from '../locales/default';
import { useTranslations } from '@/composables/useTranslations';
import { EngineTypeLabel } from '@/types';

type TransmissionMode = 'manual' | 'automatic';

const props = defineProps<{
  vehicles: any[];
  locale: string;
  parentLabel: string;
  label: string;
  translations: Record<string, string>;
  transmissionMode: TransmissionMode | null;
}>();

const { getEngineTypeTranslation } = useTranslations(
  toRef(props, 'translations'),
);

const mode = computed(() => props.transmissionMode?.toLocaleLowerCase());

type DataPoint =
  | string
  | {
      unit?: string;
      individual?: unknown;
      value?: unknown;
      min?: unknown;
      max?: unknown;
    }
  | null
  | undefined;

function getDataPointStringForLocale(dp: DataPoint, locale: string): string {
  if (dp == null) return '-';
  if (typeof dp === 'string') return dp;

  const fmt = new Intl.NumberFormat(locale);
  const unitSuffix = dp.unit ? ` ${dp.unit}` : '';

  const format = (n: any) => {
    if (typeof n === 'number') return fmt.format(n);
    if (typeof n === 'string') return n;
    return String(n);
  };

  // individual
  if (dp.individual != null) {
    return `${format(dp.individual)}${unitSuffix}`.trim();
  }

  // value
  if (dp.value != null) {
    return `${format(dp.value)}${unitSuffix}`.trim();
  }

  // range
  if (dp.min != null && dp.max != null) {
    const min = format(dp.min);
    const max = format(dp.max);

    if (dp.min === dp.max) return `${max}${unitSuffix}`.trim();
    return `${min} - ${max}${unitSuffix}`.trim();
  }

  return '';
}

function resolveDataPoint(
  parent: Record<string, any> | undefined,
  label: string,
  parentLabel: string,
) {
  const m = mode.value;

  if (!parent || !m) return undefined;

  return ['weights', 'emissionsConsumption'].includes(parentLabel)
    ? parent?.[m]?.[label]
    : parent[label];
}

const values = computed(() => {
  return (props.vehicles ?? []).map((v) => {
    const parent = (v as any)[props.parentLabel];
    const dp = resolveDataPoint(parent, props.label, props.parentLabel);
    return getDataPointStringForLocale(dp, props.locale);
  });
});

function isEngineTypeLabel(val: string): val is EngineTypeLabel {
  return ['DIESEL', 'PETROL', 'ELECTRIC'].includes(val);
}
</script>

<template>
  <tr>
    <th class="sticky-cell" scope="row">
      {{
        translations[`owvc_tech_data_${parentLabel}_${label}`] ||
        getDefaultLocaleValue(`${parentLabel}_${label}`)
      }}
    </th>
    <td v-for="(val, idx) in values" :key="idx">
      {{ isEngineTypeLabel(val) ? getEngineTypeTranslation(val) : val }}
    </td>
  </tr>
</template>

<style lang="scss" scoped>
tr {
  border-bottom: 1px solid var(--wb-table-border-color);
  text-align: right;
}

tr:last-child {
  border-bottom: none;
}

.sticky-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  text-align: start;
  background-color: white;
}

.v-has-scroll-x .sticky-cell::after {
  content: '';
  position: absolute;
  inset: -5px 3px -5px 0;
  box-shadow: 4px 0 4px -4px var(--wb-table-border-color);
  opacity: 0.8;
}

.v-has-scroll-x .sticky-cell::after {
  content: '';
  position: absolute;
  inset: -5px 3px -5px 0;
  box-shadow: 4px 0 4px -4px var(--wb-table-border-color);
  opacity: 0.8;
}

.wbx-table thead .sticky-cell {
  background: var(--table-background-striped-color);
}

.sticky-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  min-width: min(40vw, 210px);
  text-align: left;
}

.v-has-scroll-x .sticky-cell::after {
  content: '';
  position: absolute;
  inset: -5px 3px -5px 0;
  box-shadow: 4px 0 4px -4px var(--wb-table-border-color);
  opacity: 0.8;
}

.sticky-cell {
  background: white;
}

.v-has-scroll-x .sticky-cell::after {
  content: '';
  position: absolute;
  inset: -5px 3px -5px 0;
  box-shadow: 4px 0 4px -4px var(--wb-table-border-color);
  opacity: 0.8;
}

.wbx-table thead .sticky-cell {
  background: var(--table-background-striped-color);
}
</style>
